import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { utils } from '@ohif/core';
import filesToStudies from '../routes/Local/filesToStudies';
import helicalLogo from '../../assets/logo.png';

const { cachedDicomFetch } = utils;

const cachedFetch = (url: string): Promise<Response> => cachedDicomFetch(url);

async function fetchInBatches<T, R>(
  items: T[],
  maxConcurrent: number,
  fn: (item: T) => Promise<R>
): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let nextIndex = 0;

  async function worker() {
    while (nextIndex < items.length) {
      const i = nextIndex++;
      results[i] = await fn(items[i]);
    }
  }

  const workers = Array.from({ length: Math.min(maxConcurrent, items.length) }, () => worker());
  await Promise.all(workers);
  return results;
}

const HelicalCloud: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState('');
  const [statusText, setStatusText] = useState('Loading study...');
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    document.body.style.backgroundColor = 'hsl(var(--background))';
    return () => {
      document.body.style.backgroundColor = '';
    };
  }, []);

  useEffect(() => {
    const caseUuid = searchParams.get('case');
    const vt = searchParams.get('vt');
    const api = searchParams.get('api');
    const study = searchParams.get('study'); // optional: scope to one study
    const mode = searchParams.get('mode') || 'helicalcloud';

    if (!caseUuid || !vt || !api) {
      setError('SESSION_EXPIRED');
      return;
    }

    const apiBase = api.replace(/\/+$/, '');
    const auth = `case=${encodeURIComponent(caseUuid)}&vt=${encodeURIComponent(vt)}`;

    const load = async () => {
      try {
        // A cross-site frame cannot hold the CDN cookies, so no image would ever load.
        if (window.self !== window.top) {
          throw new Error(
            'This viewer cannot be embedded in a frame. Open the viewer link in a new tab.'
          );
        }

        // Step 1: fetch the whole manifest (studies, metadata, all instance URLs) in one call
        setStatusText('Fetching study list...');
        const manifestRes = await fetch(`${apiBase}/viewer/manifest?${auth}`);
        if (manifestRes.status === 401 || manifestRes.status === 403) {
          throw new Error('SESSION_EXPIRED');
        }
        if (!manifestRes.ok) {
          throw new Error(`Study list failed: ${manifestRes.status} ${manifestRes.statusText}`);
        }
        const manifestJson = await manifestRes.json();
        const apiStudies: any[] = manifestJson?.data?.studies || [];
        if (apiStudies.length === 0) {
          throw new Error('No studies found for this case.');
        }

        // Step 2: pick up the CloudFront cookies before any image is fetched
        const cookieRes = await fetch(`${apiBase}/viewer/session-cookies?${auth}`, {
          method: 'POST',
          credentials: 'include',
        });
        if (cookieRes.status === 401 || cookieRes.status === 403) {
          throw new Error('SESSION_EXPIRED');
        }
        if (cookieRes.ok) {
          const cookieJson = await cookieRes.json();
          (window as any).__helicalCloudCdnOrigin = cookieJson?.cdn ? cookieJson.origin : null;
        } else if (cookieRes.status !== 404) {
          // Without the cookies every CDN image would 403 one by one.
          throw new Error(`Session setup failed: ${cookieRes.status} ${cookieRes.statusText}`);
        }

        // Step 3: build the study list and panel metadata from the manifest
        type StudyManifest = {
          StudyInstanceUID: string;
          series: { SeriesInstanceUID: string; Images: string[] }[];
          nonDicomInstances?: { url: string; fileName: string }[];
        };
        const manifestStudies: StudyManifest[] = apiStudies.map(st => ({
          StudyInstanceUID: st.study_instance_uid,
          series: (st.series || []).map((se: any) => ({
            SeriesInstanceUID: se.series_instance_uid,
            Images: se.instances || [],
          })),
          nonDicomInstances: (st.documents || []).map((d: any) => ({
            url: d.url,
            fileName: d.name,
          })),
        }));

        // DICOM person names keep their "Last^First" caret; swap it for a space.
        const studyMetadata: Record<string, any> = {};
        for (const st of apiStudies) {
          studyMetadata[st.study_instance_uid] = {
            PatientName: String(st.patient_name || '').replace(/\^/g, ' ').trim(),
            StudyDate: st.date || '',
            StudyDescription: st.description || '',
            Modality: (st.modalities || [])[0] || '',
          };
        }

        // Step 4: keep the full manifest on window so the panel can load other studies on demand
        (window as any).__remoteLocalManifest = {
          manifest: { studies: manifestStudies },
          studyMetadata,
          folderId: null,
          patientId: null,
        };
        // Expose session credentials for the New Tab button
        (window as any).__helicalCloudSession = { case: caseUuid, vt, api: apiBase, mode };

        // Step 5: open the requested study, else the smallest one so the viewer opens fastest
        const countImages = (s: StudyManifest) =>
          s.series.reduce((n, ser) => n + ser.Images.length, 0);
        let targetStudy: StudyManifest;
        if (study) {
          targetStudy = manifestStudies.find(s => s.StudyInstanceUID === study) || manifestStudies[0];
        } else {
          targetStudy = manifestStudies.reduce((smallest, current) =>
            countImages(current) < countImages(smallest) ? current : smallest
          );
        }

        // Build URL list: 1 preview image per series; store the rest for lazy loading
        const seriesRemainingUrls = new Map<string, string[]>();
        const urls: string[] = [];
        for (const ser of targetStudy.series) {
          if (ser.Images.length > 0) {
            urls.push(ser.Images[0]);
            if (ser.Images.length > 1) {
              seriesRemainingUrls.set(ser.SeriesInstanceUID, ser.Images.slice(1));
            }
          }
        }
        if (urls.length === 0) {
          throw new Error('No images found for this case.');
        }

        // Expose lazy-load data for helicalLazyLoad.ts (runs inside the viewer)
        (window as any).__helicalSeriesRemainingUrls = seriesRemainingUrls;
        (window as any).__helicalFilesToStudies = filesToStudies;

        // Step 6: download one preview image per series and the case's documents
        const documents = targetStudy.nonDicomInstances || [];
        const total = urls.length + documents.length;
        let done = 0;
        setStatusText(`Downloading ${total} series preview${total !== 1 ? 's' : ''}...`);

        const files = await fetchInBatches(urls, 6, async (url: string) => {
          const res = await cachedFetch(url);
          if (!res.ok) {
            console.warn(`Failed to fetch image: ${url}`);
            return null;
          }
          const arrayBuffer = await res.arrayBuffer();
          done++;
          setProgress(Math.round((done / total) * 90));
          setStatusText(`Downloaded ${done} of ${total} preview${total !== 1 ? 's' : ''}...`);

          // Proxy URLs carry the sop in `key`; CDN URLs use the path.
          let fileName = 'image.dcm';
          try {
            const u = new URL(url);
            const key = u.searchParams.get('key');
            if (key) {
              fileName = `${key}.dcm`;
            } else {
              const seg = u.pathname.split('/').filter(Boolean).pop();
              if (seg) fileName = decodeURIComponent(seg);
            }
          } catch {
            /* use default */
          }
          return new File([arrayBuffer], fileName, { type: 'application/dicom' });
        });

        const documentFiles = await fetchInBatches(documents, 6, async ({ url, fileName }) => {
          const res = await cachedFetch(url);
          if (!res.ok) {
            console.warn(`Failed to fetch document: ${fileName}`);
            return null;
          }
          const arrayBuffer = await res.arrayBuffer();
          done++;
          setProgress(Math.round((done / total) * 90));
          const type = fileName.toLowerCase().endsWith('.pdf') ? 'application/pdf' : 'text/plain';
          return new File([arrayBuffer], fileName, { type });
        });

        if (!files.some(f => f !== null)) {
          throw new Error('No images could be downloaded.');
        }
        const validFiles = [...files, ...documentFiles].filter((f): f is File => f !== null);

        // Step 7: parse the files into the in-memory store
        setProgress(92);
        setStatusText(`Opening viewer...`);
        const studyUIDs = await filesToStudies(validFiles);
        setProgress(100);

        if (!studyUIDs || studyUIDs.length === 0) {
          throw new Error('No valid DICOM studies found in downloaded files.');
        }

        // Step 8: remember the session so a reload can return here
        sessionStorage.setItem('helicalcloud_case', caseUuid);
        sessionStorage.setItem('helicalcloud_can_edit', manifestJson?.data?.canEdit ? '1' : '0');
        sessionStorage.setItem('helicalcloud_vt', vt);
        sessionStorage.setItem('helicalcloud_api', apiBase);
        sessionStorage.setItem('helicalcloud_mode', mode);
        sessionStorage.setItem('helicalcloud_study', study || '');

        // Step 9: hand off to the local viewer
        const query = new URLSearchParams();
        studyUIDs.forEach(id => query.append('StudyInstanceUIDs', id));
        query.append('datasources', 'dicomlocal');

        navigate(`/${mode}/dicomlocal?${decodeURIComponent(query.toString())}`, {
          replace: true,
        });
      } catch (err) {
        console.error('HelicalCloud error:', err);
        setError(err instanceof Error ? err.message : 'An unknown error occurred.');
      }
    };

    load();
  }, [searchParams, navigate]);

  if (error) {
    const isExpired = error === 'SESSION_EXPIRED';
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-background">
        <div className="text-center max-w-sm px-4">
          <h2 className="mb-2 text-xl font-semibold text-red-400">
            {isExpired ? 'Session Expired' : 'Error'}
          </h2>
          <p className="text-gray-300">
            {isExpired
              ? 'Your viewing session has expired. Please re-open the case from the library.'
              : error}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        backgroundColor: 'hsl(var(--background))',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <img
        src={helicalLogo}
        alt="Helical"
        style={{
          height: '80px',
          marginBottom: '40px',
          opacity: 0.95,
        }}
      />
      <div
        style={{
          width: '280px',
          height: '6px',
          backgroundColor: 'hsl(var(--card))',
          borderRadius: '3px',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${progress}%`,
            background: 'linear-gradient(90deg, hsl(var(--primary)), hsl(var(--secondary)))',
            borderRadius: '3px',
            transition: 'width 0.3s ease',
          }}
        />
      </div>
      <div
        style={{
          marginTop: '12px',
          fontSize: '14px',
          fontWeight: 600,
          color: 'hsl(var(--foreground))',
          fontFamily: 'system-ui, sans-serif',
        }}
      >
        {progress}%
      </div>
      <div
        style={{
          marginTop: '6px',
          fontSize: '12px',
          color: 'hsl(var(--muted-foreground))',
          fontFamily: 'system-ui, sans-serif',
        }}
      >
        {statusText}
      </div>
    </div>
  );
};

export default HelicalCloud;
